import React, { useEffect } from 'react';
import { init as initFullStory, FullStory, isInitialized } from '@fullstory/browser';
import { getRuntimeConfig } from '@/lib/runtimeConfig';

import {
  userNamesSelector,
  userGUIDSelector,
  userIdSelector,
  isBetaUserSelector,
  isRecordingEnabledSelector,
  organizationIdSelector,
} from '@/domain/env/envSelector';
import { companyConnectedErp } from '@/domain/erpIntegration/queries/connections.select';
import { useErpConnections } from '@/domain/erpIntegration/queries/connections';
import { useSelector } from 'react-redux';
import { currentCompanyIdSelector } from '@/domain/router/routerLeafSelectors';

/**
 * Fullstory session-recording integration (DA-19623 POC).
 *
 * Headless — the application's only session recorder. Gated on
 * `isRecordingEnabledSelector` (the shared per-user profile flag) AND a
 * configured Org ID from remote runtime config (`REACT_APP_FULLSTORY_ORG_ID`).
 *
 * User-level metadata (name/email/beta) goes through `setProperties(type:'user')`.
 * Session-level context (company/organization/ERP) goes through a repeatable
 * `trackEvent` — a single session spans multiple companies/ERPs, so these are
 * recurring, searchable events, NOT one-time identify attributes. Company and
 * organization are sent as ids only (the searchable key in the dashboard); the
 * company id is the one the URL names (`currentCompanyIdSelector`), so it is
 * never null/stale while a company route is active.
 */
const Fullstory: React.FC = () => {
  const userId = useSelector(userGUIDSelector);
  const userEmail = useSelector(userIdSelector);
  const userName = useSelector(userNamesSelector);
  const isBetaUser = useSelector(isBetaUserSelector);
  const isRecordingEnabled = useSelector(isRecordingEnabledSelector);
  const companyId = useSelector(currentCompanyIdSelector);
  const organizationId = useSelector(organizationIdSelector);

  const orgId = (getRuntimeConfig().env as Record<string, string>).REACT_APP_FULLSTORY_ORG_ID;
  // Fullstory is active only when BOTH the shared recording flag is on AND an
  // Org ID is provisioned. Every effect (init, identify, context) gates on this
  // so the SDK is never touched without an identifier — matching the intended
  // "flag AND identifier" contract.
  const isFullstoryEnabled = Boolean(isRecordingEnabled && orgId);
  const { data: erpType = null } = useErpConnections(isFullstoryEnabled ? companyId : null, {
    select: companyConnectedErp,
  });

  // Initialize Fullstory (runs once when recording becomes enabled)
  useEffect(() => {
    if (!isFullstoryEnabled) {
      return;
    }

    if (!isInitialized()) {
      // Wrap init — session recorders are a common ad-blocker target; a blocked
      // or failed SDK load must never break app flow (see the pre-removal
      // Fullstory integration, which wrapped init the same way).
      try {
        initFullStory({ orgId });
        // eslint-disable-next-line no-console
        console.info('[Fullstory] initialized', { orgId });
      } catch {
        console.warn('[Fullstory] initialization failed');
      }
    }
  }, [isFullstoryEnabled, orgId]);

  // Identify user with user-level metadata (runs on user data change).
  // No `isInitialized()` guard: the SDK queues calls made after `init()`, and
  // guarding on it would race — this effect can run in the same tick as the
  // init effect (before `_fs_initialized` is set) and, since `userId` rarely
  // changes again, would then never re-fire, dropping identify entirely.
  useEffect(() => {
    if (!isFullstoryEnabled) {
      return;
    }

    FullStory('setIdentity', { uid: userId });
    FullStory('setProperties', {
      type: 'user',
      // Coerce to primitives: userEmail can be null and Fullstory user
      // properties should be primitive/non-null, or traits get
      // dropped/unsearchable.
      properties: {
        displayName: `${userName.firstName} ${userName.lastName}`,
        email: userEmail || '',
        isBetaUser: Boolean(isBetaUser),
      },
    });
  }, [userId, userName.firstName, userName.lastName, userEmail, isBetaUser, isFullstoryEnabled]);

  // Track session-level context as a recurring, searchable event so a session
  // is discoverable by every company / organization / ERP it touched.
  // No `isInitialized()` guard — same queueing/race rationale as the identify
  // effect above; context can change before `_fs_initialized` flips.
  useEffect(() => {
    if (!isFullstoryEnabled) {
      return;
    }

    // Company and ERP context are only meaningful once the route company id is
    // known — it comes from the URL, so `companyId` is present the moment a
    // company route is active. Emitting company/ERP without it would produce a
    // null-company tag that is unattributable in the dashboard, so both are
    // gated on `companyId`. Organization id is independent of the company route.
    const properties = {
      ...(companyId ? { companyId } : {}),
      ...(organizationId ? { organizationId } : {}),
      ...(erpType && companyId ? { erp: erpType } : {}),
    };

    // No-op when nothing attributable is known (e.g. an ERP is set but its
    // route company id is not yet available and no organization is active) —
    // never send a meaningless empty-properties event.
    if (Object.keys(properties).length === 0) {
      return;
    }

    FullStory('trackEvent', { name: 'dokka_context_changed', properties });

    if (erpType && companyId) {
      // eslint-disable-next-line no-console
      console.info('[Fullstory] ERP context event', { erp: erpType, companyId });
    }
  }, [companyId, organizationId, erpType, isFullstoryEnabled]);

  return null;
};

Fullstory.displayName = 'Fullstory';

export default Fullstory;
